import { bindSearchInput, debounce } from './search-input';

const NL_CENTER = [52.15, 5.35];
const NL_ZOOM = 7;
const DETAIL_ZOOM = 13;
const NEAREST_LIMIT = 12;
const POSTCODE_RE = /^[1-9][0-9]{3}\s?[A-Za-z]{2}$/;
const POSTCODE_IN_PROGRESS_RE = /^[1-9][0-9]{3}(\s?[A-Za-z]{0,1})?$/;
const PDOK_FREE_URL = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/free';
const PDOK_TILE_URL = 'https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/standaard/EPSG:3857/{z}/{x}/{y}.png';
const PDOK_ATTRIBUTION = `<a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer">CC-BY</a> <a href="https://www.kadaster.nl" target="_blank" rel="noopener noreferrer">Kadaster</a> ${new Date().getFullYear()}`;

let leafletPromise = null;

function track(eventName, properties) {
  if (!window.HSTracking || typeof window.HSTracking.track !== 'function') {
    return;
  }
  window.HSTracking.track(eventName, properties);
}

function loadLeaflet() {
  if (!leafletPromise) {
    leafletPromise = import('leaflet').then(async (leafletModule) => {
      const L = leafletModule.default || leafletModule;
      if (typeof window !== 'undefined') {
        window.L = L;
      }
      await Promise.all([
        import('leaflet/dist/leaflet.css'),
        import('leaflet.markercluster/dist/MarkerCluster.css'),
        import('leaflet.markercluster'),
      ]);
      return L;
    }).catch((err) => {
      leafletPromise = null;
      throw err;
    });
  }
  return leafletPromise;
}

function retryControl(root) {
  return root.querySelector('[data-testid="store-collection-points-retry"]');
}

function setRetryVisible(root, visible) {
  const retry = retryControl(root);
  if (!retry) {
    return;
  }
  retry.hidden = !visible;
}

function bindRetry(root) {
  const retry = retryControl(root);
  if (!retry || retry.getAttribute('data-hs-retry-bound') === '1') {
    return;
  }
  retry.setAttribute('data-hs-retry-bound', '1');
  retry.addEventListener('click', () => {
    setRetryVisible(root, false);
    setStatus(root, root.getAttribute('data-hs-text-loading') || '');
    initBlock(root);
  });
}

function normalize(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function isPostcode(value) {
  return POSTCODE_RE.test(String(value || '').trim());
}

function isPostcodeInProgress(value) {
  const query = String(value || '').trim();
  return POSTCODE_IN_PROGRESS_RE.test(query) && !isPostcode(query);
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function pointFromFeature(feature) {
  const coords = feature && feature.geometry && feature.geometry.coordinates;
  if (!coords || coords.length < 2) {
    return null;
  }
  return {
    lon: Number(coords[0]),
    lat: Number(coords[1]),
    properties: feature.properties || {},
  };
}

function parsePoint(wkt) {
  const match = String(wkt || '').match(/POINT\s*\(\s*([0-9.+-]+)\s+([0-9.+-]+)\s*\)/i);
  if (!match) {
    return null;
  }
  return { lon: Number(match[1]), lat: Number(match[2]) };
}

function compactPostcode(query) {
  return String(query || '').replace(/\s+/g, '').toUpperCase();
}

function formatPostcode(value) {
  const compact = compactPostcode(value);
  if (/^[1-9][0-9]{3}[A-Z]{2}$/.test(compact)) {
    return `${compact.slice(0, 4)} ${compact.slice(4)}`;
  }
  return String(value || '').trim();
}

function displayTitle(props) {
  const name = String((props && props.name) || '').trim();
  if (name) {
    return name;
  }
  return String((props && props.address) || '').trim();
}

function displayAddressLine(props) {
  const name = String((props && props.name) || '').trim();
  const address = String((props && props.address) || '').trim();
  const parts = [];
  if (name && address && normalize(name) !== normalize(address)) {
    parts.push(address);
  }
  const postcode = formatPostcode(props && props.postcode);
  if (postcode) {
    parts.push(postcode);
  }
  const city = String((props && props.city) || '').trim();
  if (city && normalize(city) !== normalize(address) && normalize(city) !== normalize(name)) {
    parts.push(city);
  }
  return parts.join(', ');
}

async function geocodePostcode(query) {
  // Spaced queries like "2134 XC" rank woonplaats hits (Oude-Tonge) above the
  // postcode centroid. Compact and restrict to type:postcode.
  const compact = compactPostcode(query);
  const url = `${PDOK_FREE_URL}?q=${encodeURIComponent(compact)}&rows=1&fq=${encodeURIComponent('type:postcode')}`;
  const response = await fetch(url, { credentials: 'omit' });
  if (!response.ok) {
    throw new Error('pdok_free_failed');
  }
  const payload = await response.json();
  const docs = (((payload || {}).response) || {}).docs || [];
  if (!docs.length) {
    return null;
  }
  return parsePoint(docs[0].centroide_ll);
}

function createMarkerIcon(L) {
  return L.divIcon({
    className: 'store-collection-points__pin',
    html: '<span class="store-collection-points__pin-dot"></span>',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -12],
  });
}

function createOriginIcon(L, label) {
  const title = escapeHtml(label || '');
  return L.divIcon({
    className: 'store-collection-points__origin',
    html: `<span class="store-collection-points__origin-dot" data-testid="store-collection-points-origin" title="${title}"></span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function popupHtml(point, root) {
  const hoursLabel = root.getAttribute('data-hs-text-opening-hours') || '';
  const detailsLabel = root.getAttribute('data-hs-text-details') || '';
  const props = point.properties || {};
  const title = displayTitle(props);
  const addressLine = displayAddressLine(props);
  const lines = [];
  if (title) {
    lines.push(`<strong>${escapeHtml(title)}</strong>`);
  }
  if (addressLine) {
    lines.push(escapeHtml(addressLine));
  }
  if (props.opening_hours) {
    lines.push(`${escapeHtml(hoursLabel)}: ${escapeHtml(props.opening_hours)}`);
  }
  if (props.details) {
    lines.push(`${escapeHtml(detailsLabel)}: ${escapeHtml(props.details)}`);
  }
  return `<div class="store-collection-points__popup">${lines.join('<br>')}</div>`;
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatResults(root, count) {
  if (count === 1) {
    return root.getAttribute('data-hs-text-results-one') || '1';
  }
  return (root.getAttribute('data-hs-text-results-many') || '%s').replace('%s', String(count));
}

function renderResults(root, points, origin) {
  const list = root.querySelector('[data-testid="store-collection-points-list"]');
  const results = root.querySelector('[data-testid="store-collection-points-results"]');
  const empty = root.querySelector('[data-testid="store-collection-points-empty"]');
  const heading = root.querySelector('[data-testid="store-collection-points-results-heading"]');
  const template = root.querySelector('[data-hs-result-template]');
  if (!list || !results || !empty || !template) {
    return;
  }

  list.innerHTML = '';
  if (!points.length) {
    results.hidden = true;
    empty.hidden = false;
    return;
  }

  empty.hidden = true;
  results.hidden = false;
  if (heading) {
    heading.textContent = formatResults(root, points.length);
  }

  const hoursLabel = root.getAttribute('data-hs-text-opening-hours') || '';
  const detailsLabel = root.getAttribute('data-hs-text-details') || '';
  const sorted = origin
    ? points.slice().sort((a, b) => (
      haversineKm(origin.lat, origin.lon, a.lat, a.lon)
      - haversineKm(origin.lat, origin.lon, b.lat, b.lon)
    ))
    : points;

  sorted.slice(0, NEAREST_LIMIT).forEach((point) => {
    const node = template.content.firstElementChild.cloneNode(true);
    const button = node.querySelector('.store-collection-points__item-button');
    const props = point.properties || {};
    node.querySelector('.store-collection-points__item-name').textContent = displayTitle(props);
    node.querySelector('.store-collection-points__item-address').textContent = displayAddressLine(props);
    const hours = node.querySelector('.store-collection-points__item-hours');
    const details = node.querySelector('.store-collection-points__item-details');
    hours.textContent = props.opening_hours ? `${hoursLabel}: ${props.opening_hours}` : '';
    details.textContent = props.details ? `${detailsLabel}: ${props.details}` : '';
    button.addEventListener('click', () => {
      root.dispatchEvent(new CustomEvent('hs:collection-point-open', { detail: point }));
    });
    list.appendChild(node);
  });
}

function cityMatches(city, needle) {
  // Prefix on the city field only. Substring would treat Nieuw-Amsterdam as Amsterdam.
  return Boolean(city) && (city === needle || city.startsWith(needle));
}

function filterByQuery(points, query) {
  const needle = normalize(query);
  if (!needle) {
    return points;
  }
  const byCity = points.filter((point) => cityMatches(normalize(point.properties.city), needle));
  if (byCity.length) {
    return byCity;
  }
  const compactNeedle = compactPostcode(query);
  if (/^[1-9][0-9]{3}[A-Z]{2}$/.test(compactNeedle)) {
    const byPostcode = points.filter((point) => (
      compactPostcode(point.properties.postcode) === compactNeedle
    ));
    if (byPostcode.length) {
      return byPostcode;
    }
  }
  return points.filter((point) => {
    const haystack = normalize([
      point.properties.name,
      point.properties.address,
    ].join(' '));
    return haystack.indexOf(needle) !== -1;
  });
}

function setStatus(root, message) {
  const status = root.querySelector('[data-testid="store-collection-points-status"]');
  if (!status) {
    return;
  }
  if (!message) {
    status.hidden = true;
    status.textContent = '';
    return;
  }
  status.hidden = false;
  status.textContent = message;
}

function currentPageUrl() {
  try {
    return new URL(window.location.href);
  } catch (err) {
    return null;
  }
}

function toSameOriginUrl(raw) {
  try {
    const url = new URL(raw, window.location.href);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (err) {
    return raw;
  }
}

// http://store.healsens.local is not a secure origin, so Chrome denies GPS.
// *.localhost is a secure context on HTTP; keep the path and continue locate.
function upgradeInsecureLocalStoreUrl() {
  if (window.isSecureContext) {
    return '';
  }
  const url = currentPageUrl();
  if (!url || !url.hostname.endsWith('.healsens.local')) {
    return '';
  }
  url.hostname = url.hostname.replace(/\.local$/, '.localhost');
  url.searchParams.set('hs_locate', '1');
  return url.href;
}

function consumeLocateQuery() {
  const url = currentPageUrl();
  if (!url || url.searchParams.get('hs_locate') !== '1') {
    return false;
  }
  url.searchParams.delete('hs_locate');
  const next = url.pathname + url.search + url.hash;
  if (window.history && typeof window.history.replaceState === 'function') {
    window.history.replaceState({}, '', next);
  }
  return true;
}

async function initBlock(root) {
  if (root.getAttribute('data-hs-ready') === '1') {
    return;
  }
  root.setAttribute('data-hs-ready', '1');

  const entrypoint = root.getAttribute('data-hs-entrypoint') || 'page';
  const locationsUrl = toSameOriginUrl(root.getAttribute('data-hs-locations-url') || '');
  const mapEl = root.querySelector('[data-testid="store-collection-points-map"]');
  const form = root.querySelector('[data-testid="store-collection-points-search"]');
  const queryInput = root.querySelector('[data-testid="store-collection-points-query"]');
  const locateButton = root.querySelector('[data-testid="store-collection-points-locate"]');
  if (!mapEl || !locationsUrl) {
    return;
  }

  setStatus(root, root.getAttribute('data-hs-text-loading') || '');

  let L;
  let geojson;
  try {
    L = await loadLeaflet();
    const response = await fetch(locationsUrl, { credentials: 'same-origin' });
    if (!response.ok) {
      throw new Error('locations');
    }
    geojson = await response.json();
  } catch (err) {
    root.removeAttribute('data-hs-ready');
    setStatus(root, root.getAttribute('data-hs-text-load-error') || '');
    setRetryVisible(root, true);
    return;
  }
  const points = ((geojson && geojson.features) || []).map(pointFromFeature).filter(Boolean);
  setRetryVisible(root, false);
  setStatus(root, '');

  const map = L.map(mapEl, {
    zoomControl: true,
    attributionControl: true,
    scrollWheelZoom: false,
  }).setView(NL_CENTER, NL_ZOOM);

  L.tileLayer(PDOK_TILE_URL, {
    attribution: PDOK_ATTRIBUTION,
    minZoom: 6,
    maxZoom: 14,
    bounds: L.latLngBounds([50.5, 3.2], [53.7, 7.4]),
  }).addTo(map);

  const icon = createMarkerIcon(L);
  const originIcon = createOriginIcon(
    L,
    root.getAttribute('data-hs-text-your-location') || ''
  );
  const cluster = L.markerClusterGroup({
    showCoverageOnHover: false,
    maxClusterRadius: 60,
    spiderfyOnMaxZoom: true,
  });
  const markersById = {};
  let originMarker = null;

  function setOriginMarker(origin) {
    if (originMarker) {
      map.removeLayer(originMarker);
      originMarker = null;
    }
    if (!origin) {
      return;
    }
    originMarker = L.marker([origin.lat, origin.lon], {
      icon: originIcon,
      interactive: false,
      keyboard: false,
      zIndexOffset: 1000,
    });
    originMarker.addTo(map);
  }

  points.forEach((point) => {
    const marker = L.marker([point.lat, point.lon], { icon });
    marker.bindPopup(popupHtml(point, root));
    marker.on('click', () => {
      track('shop:collection_points_pin_open', {
        entrypoint,
        pin_id: String(point.properties.id || ''),
      });
    });
    cluster.addLayer(marker);
    markersById[String(point.properties.id)] = marker;
  });
  map.addLayer(cluster);
  requestAnimationFrame(() => map.invalidateSize());

  track('shop:collection_points_view', {
    entrypoint,
    result_count: points.length,
  });

  function focusPoints(matches, origin, queryKind) {
    setOriginMarker(origin);
    renderResults(root, matches, origin);
    if (!matches.length) {
      track('shop:collection_points_search', {
        entrypoint,
        query_kind: queryKind,
        result_count: 0,
      });
      return;
    }
    const boundsPoints = matches.map((point) => [point.lat, point.lon]);
    if (origin) {
      boundsPoints.push([origin.lat, origin.lon]);
    }
    const bounds = L.latLngBounds(boundsPoints);
    if (matches.length === 1 && !origin) {
      map.setView([matches[0].lat, matches[0].lon], DETAIL_ZOOM);
    } else if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [24, 24], maxZoom: DETAIL_ZOOM });
    }
    track('shop:collection_points_search', {
      entrypoint,
      query_kind: queryKind,
      result_count: matches.length,
    });
  }

  root.addEventListener('hs:collection-point-open', (event) => {
    const point = event.detail;
    const marker = markersById[String(point.properties.id)];
    if (marker) {
      cluster.zoomToShowLayer(marker, () => marker.openPopup());
    }
    track('shop:collection_points_pin_open', {
      entrypoint,
      pin_id: String(point.properties.id || ''),
    });
  });

  let searchSequence = 0;

  async function runSearch(rawQuery) {
    const sequence = ++searchSequence;
    const query = String(rawQuery || '').trim();
    if (!query) {
      renderResults(root, [], null);
      root.querySelector('[data-testid="store-collection-points-empty"]').hidden = true;
      setOriginMarker(null);
      map.setView(NL_CENTER, NL_ZOOM);
      return;
    }

    if (isPostcode(query)) {
      try {
        const origin = await geocodePostcode(query);
        if (sequence !== searchSequence) {
          return;
        }
        if (!origin) {
          focusPoints([], null, 'postcode');
          return;
        }
        const nearest = points
          .map((point) => ({
            ...point,
            distanceKm: haversineKm(origin.lat, origin.lon, point.lat, point.lon),
          }))
          .sort((a, b) => a.distanceKm - b.distanceKm)
          .slice(0, NEAREST_LIMIT);
        map.setView([origin.lat, origin.lon], DETAIL_ZOOM);
        focusPoints(nearest, origin, 'postcode');
      } catch (err) {
        if (sequence !== searchSequence) {
          return;
        }
        focusPoints(filterByQuery(points, query), null, 'postcode');
      }
      return;
    }

    focusPoints(filterByQuery(points, query), null, 'city');
  }

  if (form) {
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      runSearch(queryInput ? queryInput.value : '');
    });
  }

  if (queryInput) {
    bindSearchInput(queryInput, debounce((value) => {
      const query = String(value || '').trim();
      if (isPostcodeInProgress(query)) {
        return;
      }
      runSearch(query);
    }, 200));
  }

  function requestLocation() {
    const sequence = ++searchSequence;
    track('shop:collection_points_use_my_location', {
      entrypoint,
      status: 'clicked',
    });
    if (!navigator.geolocation) {
      track('shop:collection_points_use_my_location', {
        entrypoint,
        status: 'denied',
      });
      setStatus(root, root.getAttribute('data-hs-text-location-denied') || '');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (sequence !== searchSequence) {
          return;
        }
        track('shop:collection_points_use_my_location', {
          entrypoint,
          status: 'granted',
        });
        const origin = {
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        };
        const nearest = points
          .map((point) => ({
            ...point,
            distanceKm: haversineKm(origin.lat, origin.lon, point.lat, point.lon),
          }))
          .sort((a, b) => a.distanceKm - b.distanceKm)
          .slice(0, NEAREST_LIMIT);
        map.setView([origin.lat, origin.lon], DETAIL_ZOOM);
        focusPoints(nearest, origin, 'geolocation');
      },
      () => {
        if (sequence !== searchSequence) {
          return;
        }
        track('shop:collection_points_use_my_location', {
          entrypoint,
          status: 'denied',
        });
        setStatus(root, root.getAttribute('data-hs-text-location-denied') || '');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 0 }
    );
  }

  if (locateButton) {
    locateButton.addEventListener('click', () => {
      const upgraded = upgradeInsecureLocalStoreUrl();
      if (upgraded) {
        window.location.assign(upgraded);
        return;
      }
      requestLocation();
    });
  }

  if (consumeLocateQuery()) {
    requestLocation();
  }
}

function watch(root) {
  bindRetry(root);
  const eager = root.getAttribute('data-hs-eager') === '1';
  if (eager || !('IntersectionObserver' in window)) {
    initBlock(root);
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        observer.disconnect();
        initBlock(root);
      }
    });
  }, { rootMargin: '200px 0px' });
  observer.observe(root);
}

function initAll() {
  document.querySelectorAll('[data-hs-collection-points]').forEach(watch);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
